import { useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { api } from './lib/api';
import { useThemeHotkey } from './lib/theme';
import { R, ft, shadow } from './lib/ui';
import { Sidebar } from './components/Sidebar';
import { ToastProvider } from './components/Toast';
import { DialogHost } from './components/DialogHost';
import { Spinner } from './components/Spinner';
import { Login, NoAccess } from './pages/Login';
import { DocList } from './pages/DocList';
import { DocView } from './pages/DocView';
import { PublicView } from './pages/PublicView';
import { FeedbackPage } from './pages/Feedback';
import { AccessPage } from './pages/Access';
import { ForceChange } from './pages/ForceChange';

import { PublicPortal } from './pages/PublicPortal';
import { PortalAdmin } from './pages/PortalAdmin';
import { ErrorBoundary } from './components/ErrorBoundary';

/** Узкий экран прячет боковую панель в выдвижную шторку. */
function useNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(
    typeof window === 'undefined' ? false : window.innerWidth < breakpoint,
  );

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  return narrow;
}

export default function App() {
  const [location] = useLocation();
  useThemeHotkey();

  // Публичные маршруты рисуются без сессии и без боковой панели.
  const isPublic =
    location.startsWith('/shared/') ||
    location.startsWith('/p/') ||
    location === '/portal' ||
    location === '/registry' ||
    (location.startsWith('/portal/') && !location.startsWith('/portal-admin')) ||
    location.startsWith('/registry/');

  if (isPublic) {
    return (
      <ToastProvider>
        <DialogHost>
          <ErrorBoundary>
            <Switch>
              <Route path="/portal" component={PublicPortal} />
              <Route path="/registry" component={PublicPortal} />
              <Route path="/shared/:token">
                {(p) => (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100dvh',
                      width: '100%',
                      overflow: 'hidden',
                      fontSize: 14,
                      background: R.bg,
                    }}
                  >
                    <PublicView token={p.token} kind="shared" />
                  </div>
                )}
              </Route>
              <Route path="/p/:token">
                {(p) => (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100dvh',
                      width: '100%',
                      overflow: 'hidden',
                      fontSize: 14,
                      background: R.bg,
                    }}
                  >
                    <PublicView token={p.token} kind="public" />
                  </div>
                )}
              </Route>
              <Route>
                {() => (
                  <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
                    404 — страница не найдена
                  </div>
                )}
              </Route>
            </Switch>
          </ErrorBoundary>
        </DialogHost>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <DialogHost>
        <Shell />
      </DialogHost>
    </ToastProvider>
  );
}

function Shell() {
  const [location, navigate] = useLocation();
  const narrow = useNarrow();
  const [drawer, setDrawer] = useState(false);

  const me = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  const docs = useQuery({ queryKey: ['docs'], queryFn: api.docs, enabled: !!me.data });

  useEffect(() => setDrawer(false), [location]);
  useEffect(() => {
    if (!narrow) setDrawer(false);
  }, [narrow]);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);

  if (me.isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100dvh', background: R.bg }}>
        <Spinner />
      </div>
    );
  }

  if (me.error || !me.data) return <Login />;

  const user = me.data;
  
  if (user.mustChangePassword) {
    return <ForceChange />;
  }

  const isAdmin = user.role === 'admin';
  const canEdit = user.role === 'admin' || user.role === 'editor';

  const sidebar = (
    <Sidebar
      me={user}
      docs={docs.data ?? []}
      loading={docs.isLoading}
      onNavigate={(path) => {
        navigate(path);
        setDrawer(false);
      }}
    />
  );

  return (
    <div
      style={{
        display: 'flex',
        height: '100dvh',
        width: '100%',
        overflow: 'hidden',
        fontSize: 14,
        background: R.bg,
      }}
    >
      {!narrow && sidebar}

      {narrow && drawer && (
        <>
          <div
            onClick={() => setDrawer(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,17,.5)', zIndex: 120 }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              left: 0,
              zIndex: 121,
              boxShadow: shadow.panel,
              display: 'flex',
            }}
          >
            {sidebar}
          </div>
        </>
      )}

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          height: '100%',
          background: R.bg,
        }}
      >
        {narrow && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: '0 0 auto',
              minHeight: 44,
              padding: '0 14px',
              borderBottom: ft.strong,
              background: R.bgSubtle,
            }}
          >
            <button
              onClick={() => setDrawer(true)}
              title="Документы"
              style={{
                display: 'grid',
                placeItems: 'center',
                width: 30,
                height: 30,
                border: ft.edge,
                color: R.text,
              }}
            >
              <Menu size={16} />
            </button>
            <span style={{ fontWeight: 800, fontSize: 14, color: R.text }}>Redwood Tables</span>
          </div>
        )}

        <ErrorBoundary>
          <Switch>
            <Route path="/" component={DocList} />
            <Route path="/d/:docId/t/:tableId">
              {(p) => <DocView docId={p.docId} tableId={p.tableId} canEdit={canEdit} />}
            </Route>
            <Route path="/d/:docId">{(p) => <DocView docId={p.docId} canEdit={canEdit} />}</Route>
            <Route path="/feedback" component={FeedbackPage} />
            <Route path="/access">{() => (isAdmin ? <AccessPage /> : <NoAccess />)}</Route>
            <Route path="/portal-admin">{() => (isAdmin ? <PortalAdmin /> : <NoAccess />)}</Route>
            <Route>
              {() => (
                <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
                  404 — страница не найдена
                </div>
              )}
            </Route>
          </Switch>
        </ErrorBoundary>
      </main>
    </div>
  );
}
